import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import productService from './productService';
import { toast } from 'react-toastify';

const CART_STORAGE_KEY = 'cart';
const CART_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
const MARKET_LABELS = {
    latvia: 'Latvia',
    uk: 'United Kingdom',
};

const loadCartFromStorage = () => {
    try {
        const raw = localStorage.getItem(CART_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);

        // Legacy format: array of items without metadata
        if (Array.isArray(parsed)) {
            localStorage.removeItem(CART_STORAGE_KEY);
            return [];
        }

        const { items, updatedAt } = parsed || {};
        if (!Array.isArray(items)) return [];

        if (typeof updatedAt !== 'number' || Date.now() - updatedAt > CART_EXPIRY_MS) {
            localStorage.removeItem(CART_STORAGE_KEY);
            return [];
        }

        return items;
    } catch (error) {
        console.error('Failed to load cart from storage', error);
        return [];
    }
};

const persistCart = (items) => {
    const payload = {
        items,
        updatedAt: Date.now(),
    };
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(payload));
};

const cart = loadCartFromStorage();

const pruneCartForMarket = (state, availableProducts, marketKey) => {
    if (!Array.isArray(state.cart)) return 0;

    const availableIds = new Set(
        (availableProducts || []).map((product) => product._id)
    );

    const before = state.cart.length;
    state.cart = state.cart.filter((item) => availableIds.has(item._id));

    if (before !== state.cart.length) {
        persistCart(state.cart);
        const removedCount = before - state.cart.length;
        const marketLabel = MARKET_LABELS[marketKey?.toLowerCase()] || 'this market';
        toast.info(`Removed ${removedCount} item(s) not available in ${marketLabel}.`);
        return removedCount;
    }

    return 0;
};

// This base state will be used for resets
const baseInitialState = {
    products: [],
    product: null,
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: '',
};

// This initialState is used only for the very first app load
const initialState = {
    ...baseInitialState,
    cart: cart,
};

// Async Thunk for creating a product with all image uploads
export const createProduct = createAsyncThunk(
    'product/create',
    async (productData, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;

            // --- Comprehensive Image Uploading ---
            
            // 1. Upload main image
            const mainImageName = await productService.uploadImage(productData.image);

            // 2. Upload gallery images in parallel
            const galleryImagePromises = productData.images.map(file => productService.uploadImage(file));
            const galleryImageNames = await Promise.all(galleryImagePromises);

            // 3. Upload variant-specific images in parallel
            const variantImagePromises = productData.variants.map(variant => productService.uploadImage(variant.image));
            const variantImageNames = await Promise.all(variantImagePromises);
            
            // 4. Assemble the final data payload, replacing File objects with their uploaded filenames
            const finalVariants = productData.variants.map((variant, index) => ({
                ...variant,
                image: variantImageNames[index], // Replace File with filename string
            }));

            const finalProductData = {
                ...productData,
                image: mainImageName,
                images: galleryImageNames,
                variants: finalVariants,
            };

            // 5. Send the complete, cleaned data to the backend
            return await productService.addProduct(finalProductData, token);

        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Update product
export const updateProduct = createAsyncThunk(
    'product/update',
    async ({ id, productData }, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;

            // --- Handle Image Uploads (only upload new File objects) ---
            
            // 1. Upload main image if it's a new File
            const mainImageName = await productService.uploadImage(productData.image);

            // 2. Upload gallery images (only new File objects)
            const galleryImagePromises = productData.images.map(file => productService.uploadImage(file));
            const galleryImageNames = await Promise.all(galleryImagePromises);

            // 3. Upload variant-specific images (only new File objects)
            const variantImagePromises = productData.variants.map(variant => productService.uploadImage(variant.image));
            const variantImageNames = await Promise.all(variantImagePromises);
            
            // 4. Assemble the final data payload
            const finalVariants = productData.variants.map((variant, index) => ({
                ...variant,
                image: variantImageNames[index], // This will be the original string if no new file was uploaded
            }));

            const finalProductData = {
                ...productData,
                image: mainImageName,
                images: galleryImageNames,
                variants: finalVariants,
            };

            // 5. Send the update request
            return await productService.updateProduct(id, finalProductData, token);

        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async Thunk for getting all admin products
export const getAdminProducts = createAsyncThunk(
    'product/getAllAdmin',
    async (_, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await productService.getAdminProducts(token);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Get product by ID
export const getProductById = createAsyncThunk(
    'product/getById',
    async (id, thunkAPI) => {
        try {
            return await productService.getProductById(id);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Delete product
export const deleteProduct = createAsyncThunk(
    'product/delete',
    async (id, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await productService.deleteProduct(id, token);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const restoreProduct = createAsyncThunk(
    'product/restore',
    async (id, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await productService.restoreProduct(id, token);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Get products
export const getProducts = createAsyncThunk(
    'product/getAll',
    async (market, thunkAPI) => {
        try {
            return await productService.getProducts(market);
        } catch (error) {
            const message = (error.response?.data?.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const productSlice = createSlice({
    name: 'product',
    initialState,
    reducers: {
        reset: (state) => {
            Object.assign(state, baseInitialState);
            state.cart = loadCartFromStorage();
        },
        resetProduct: (state) => {
            state.product = null;
            state.isError = false;
            state.isSuccess = false;
            state.isLoading = false; // Stop loading if navigating away
            state.message = '';
        },
        addToCart: (state, action) => {
            const newItem = {
                ...action.payload,
                countries: Array.isArray(action.payload.countries) ? action.payload.countries : [],
            };
            const existingItemIndex = state.cart.findIndex(
                (item) => item._id === newItem._id && item.variant._id === newItem.variant._id
            );

            if (existingItemIndex !== -1) {
                // Item already exists, just update quantity
                state.cart[existingItemIndex].quantity += newItem.quantity;
                state.cart[existingItemIndex].countries = newItem.countries;
            } else {
                // Item is new, add to cart
                state.cart.push(newItem);
            }
            persistCart(state.cart);
        },

        /**
         * Removes an item from the cart completely.
         * action.payload: { productId, variantId }
         */
        removeFromCart: (state, action) => {
            const { productId, variantId } = action.payload;
            state.cart = state.cart.filter(
                (item) => !(item._id === productId && item.variant._id === variantId)
            );
            persistCart(state.cart);
        },

        /**
         * Updates the quantity of a specific item in the cart.
         * If quantity is 0 or less, removes the item.
         * action.payload: { productId, variantId, newQuantity }
         */
        updateCartQuantity: (state, action) => {
            const { productId, variantId, newQuantity } = action.payload;
            const itemIndex = state.cart.findIndex(
                (item) => item._id === productId && item.variant._id === variantId
            );

            if (itemIndex !== -1) {
                if (newQuantity > 0) {
                    state.cart[itemIndex].quantity = newQuantity;
                } else {
                    // Remove item if quantity is 0 or less
                    state.cart.splice(itemIndex, 1);
                }
                persistCart(state.cart);
            }
        },
        clearCart: (state) => {
            state.cart = [];
            localStorage.removeItem(CART_STORAGE_KEY);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createProduct.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
                state.isError = false;
            })
            .addCase(createProduct.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.products.push(action.payload);
                toast.success('Product created successfully');
                window.location.href = '/admin/products';
            })
            .addCase(createProduct.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(getAdminProducts.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getAdminProducts.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.products = action.payload;
            })
            .addCase(getAdminProducts.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(getProductById.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getProductById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.product = action.payload;
            })
            .addCase(getProductById.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(deleteProduct.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteProduct.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                const index = state.products.findIndex(product => product._id === action.payload._id);
                if (index !== -1) {
                    state.products[index] = action.payload;
                }
                toast.success('Product moved to trash');
            })
            .addCase(deleteProduct.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(restoreProduct.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(restoreProduct.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                const index = state.products.findIndex(product => product._id === action.payload._id);
                if (index !== -1) {
                    state.products[index] = action.payload;
                } else {
                    state.products.push(action.payload);
                }
                toast.success('Product restored');
            })
            .addCase(restoreProduct.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(updateProduct.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
                state.isError = false;
            })
            .addCase(updateProduct.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                // Update the product in the products array
                const index = state.products.findIndex(product => product._id === action.payload._id);
                if (index !== -1) {
                    state.products[index] = action.payload;
                }
                toast.success('Product updated successfully');
                window.location.href = '/admin/products';
            })
            .addCase(updateProduct.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            .addCase(getProducts.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getProducts.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.products = action.payload;
                pruneCartForMarket(state, action.payload, action.meta?.arg);
            })
            .addCase(getProducts.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            });
    },
});

export const { reset, resetProduct, addToCart, removeFromCart, updateCartQuantity, clearCart } = productSlice.actions;
export default productSlice.reducer;
